import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { debounce } from "lodash";
// @mui
import { Container, Typography, Stack, Chip, Switch, FormControlLabel, TextField, Button, InputAdornment } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
// components
import { useSettingsContext } from '../../components/settings';
import CustomPagination from '../../components/custom-pagination';
import Iconify from '../../components/iconify';
import CustomModal from "../../components/custom-modal";
import MedicationView from './medicationView';
import CustomGrid from '../../components/custom-grid';

import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { getListColumns } from './medicationColumns';
import { getPortalMedication } from '../../APIs/medication';

// ----------------------------------------------------------------------
import { useAuthContext } from '../../auth/useAuthContext';

export default function MedicationList() {
    const { themeStretch } = useSettingsContext();
    const axiosPrivate = useAxiosPrivate();    
    const [includeOnlyActive, setIncludeOnlyActive] = useState(false);
    const [totalMedicaitons, setTotalMedicaitons] = useState(0);
    const [medications, setMedications] = useState([]);    
    const [term, setTerm] = useState();
    const [fullWidth] = useState(true);
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(20);
    const [openModal, setOpenModal] = useState(false);
    const [selectedMedication, setSelectedMedication] = useState();
    const [readOnly, setReadOnly] = useState(true);
    
    const getMedication = (currentPage = 1) => {
        const request = { includeOnlyActive,term, size, page: currentPage ?? page }        
        setPage(request.page);

        getPortalMedication(axiosPrivate, request, (response) => {
            const { data, total } = response;
            setMedications(data);
            setTotalMedicaitons(total);
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(getMedication, [includeOnlyActive,term, size]);    

    const handleChange = (event) => {
        setIncludeOnlyActive(event.target.checked);
    };

    const handleSearch = (event) => {
        setTerm(event.target.value);
    }

    const handlePageChange = (pageIndex) => {
        setPage(pageIndex);
        getMedication(pageIndex);
    }

    const handlePageSizeChange = (pageSize) => {
        setSize(pageSize);
    }

    const onHandleClose = () => {
        setSelectedMedication();
        setReadOnly(true);
        setOpenModal(false);
    }

    const handleRowClick = ({ row }) => {
        setSelectedMedication(row);
        setOpenModal(true);
        setReadOnly(true);
    }

    const onEdit = (id) => {
        const medication = medications.find(c => c.id === id);
        setSelectedMedication(medication);
        setReadOnly(false);
        setOpenModal(true);
    }

    const onAdd = () => {
        setReadOnly(false);
        setOpenModal(true);
    }

    const handleMedication = () => {
        getMedication(page);
        onHandleClose();
    }

    const debouncedChangeHandler = debounce(handleSearch, 500);

    const columns = getListColumns({ onEdit });

    return (
        <>
            <Helmet>
                <title>Medication | flok</title>
            </Helmet>

            <Container maxWidth={themeStretch ? false : 'xl'}>
                <Stack
                    flexGrow={1}
                    direction="column"
                    justifyContent="flex-start"
                    spacing={{ xs: 0.5, sm: 1.5 }}>
                    <Typography variant="h4" component="h4" paragraph>
                        Medication <Chip label={totalMedicaitons} />
                        <Button style={{ marginLeft: '2%', minWidth: 0 }} variant="outlined" onClick={onAdd}>+</Button>
                    </Typography>
                    <Stack
                        flexGrow={1}
                        direction="row"
                        justifyContent="flex-start"
                        spacing={{ xs: 0.5, sm: 1.5 }}>
                        <TextField
                            hiddenLabel
                            placeholder="Search..."
                            size="small"
                            onChange={debouncedChangeHandler}
                            fullWidth={fullWidth}
                            defaultValue={term}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={includeOnlyActive}
                                    onChange={handleChange}
                                    inputProps={{ 'aria-label': 'controlled' }} />
                            }
                            label="Hide Deleted Labs" />
                    </Stack>
                    <CustomGrid>
                        <DataGrid
                            sx={{
                                '.MuiDataGrid-columnHeaders': {
                                    backgroundColor: 'lightgray'
                                },
                                '.MuiDataGrid-columnSeparator': {
                                    display: 'none',
                                },
                                '&.MuiDataGrid-root': {
                                    border: 'none'
                                },
                                '.MuiDataGrid-row': {
                                    cursor: 'pointer'
                                },
                                '.MuiDataGrid-footerContainer': {
                                    display: 'none'
                                }
                            }}
                            rows={medications}
                            columns={columns}
                            disableColumnMenu
                            disableColumnFilter
                            disableRowSelectionOnClick
                            hideFooterPagination
                            onRowClick={handleRowClick}
                        />
                    </CustomGrid>
                    <CustomPagination pageSize={size} currentPage={page} total={totalMedicaitons} handleChange={handlePageChange} handlePageSizeChange={handlePageSizeChange} />
                    <CustomModal open={openModal} onClose={onHandleClose}>
                        <MedicationView onChange={handleMedication} item={selectedMedication} onHandleClose={onHandleClose} readOnly={readOnly} />
                    </CustomModal>
                </Stack>
            </Container>
        </>
    );
}

